import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, X } from 'lucide-react';
import { fmtArgent } from '@/lib/lmdsDeductions';

const API = process.env.REACT_APP_BACKEND_URL;

// 🧾 Phase 5 : surlignage du terme (insensible casse/accents) dans un texte.
const norm = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export function Surligne({ texte, terme }) {
  const t = texte || '';
  const i = norm(t).indexOf(norm(terme));
  if (!terme || i < 0) return <>{t}</>;
  return <>{t.slice(0, i)}<mark className="bg-yellow-200 rounded px-0.5" data-testid="recherche-surligne">{t.slice(i, i + terme.length)}</mark>{t.slice(i + terme.length)}</>;
}

const fmtDate = (iso) => (iso ? new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'short' }).format(new Date(iso + 'T12:00:00')) : '');
const CLASSES_STATUT = { payee: 'bg-emerald-100 text-emerald-800', non_payee: 'bg-red-100 text-red-800', payee_apres_retard: 'bg-emerald-50 text-emerald-700', deleguee: 'bg-slate-100 text-slate-600' };

// 🧾 Phase 5 : barre de recherche globale client du Marché du Store (identité appliquée côté API).
export function RechercheClientGlobale({ installateurId, onOuvrir, minCaracteres = 2 }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < minCaracteres) { setRes(null); setErreur(''); return; }
    timer.current = setTimeout(async () => {
      setChargement(true); setErreur('');
      try {
        const r = await axios.get(`${API}/api/lmds/recherche-clients`, { params: { q: q.trim(), ...(installateurId ? { installateur_id: installateurId } : {}) } });
        setRes(r.data);
      } catch (e) { setErreur(e.response?.data?.detail || 'Recherche impossible.'); }
      finally { setChargement(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q, installateurId, minCaracteres]);

  return (
    <div className="relative" data-testid="recherche-client-globale">
      <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 h-10 shadow-sm">
        <Search className="w-4 h-4 text-slate-400" />
        <input className="flex-1 outline-none text-sm bg-transparent" placeholder="Rechercher un client dans tous les rapports…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="recherche-client-input" aria-label="Recherche client" />
        {q && <button onClick={() => setQ('')} data-testid="recherche-client-effacer" aria-label="Effacer"><X className="w-4 h-4 text-slate-400" /></button>}
      </div>
      {(res || chargement || erreur) && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-auto" data-testid="recherche-client-resultats">
          {chargement && <p className="p-3 text-sm text-slate-500" data-testid="recherche-client-chargement">Recherche…</p>}
          {erreur && <p className="p-3 text-sm text-red-600" data-testid="recherche-client-erreur">{erreur}</p>}
          {res && !chargement && (
            <>
              <p className="px-3 py-2 text-xs text-slate-500 border-b" data-testid="recherche-client-compteur">
                {res.total === 0 ? 'Aucun client trouvé.' : `${res.total} résultat${res.total > 1 ? 's' : ''} pour « ${res.q} »`}
              </p>
              <ul>
                {res.resultats.map((r) => (
                  <li key={`${r.rapport_id}-${r.ligne_id}`}>
                    <button className="w-full text-left px-3 py-2 hover:bg-slate-50 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm border-b border-slate-100"
                      onClick={() => { onOuvrir(r.lien); setQ(''); }} data-testid={`recherche-resultat-${r.ligne_id}`}>
                      <span className="font-semibold min-w-[10rem]"><Surligne texte={r.client_nom} terme={q.trim()} /></span>
                      <span className="text-slate-600">{r.installateur.nom}</span>
                      <span className="text-slate-500 text-xs">sem. {r.semaine} ({r.annee}) · {fmtDate(r.dates_semaine.debut)} → {fmtDate(r.dates_semaine.fin)}{r.date_ligne ? ` · ${fmtDate(r.date_ligne)}` : ''}</span>
                      <span className="ml-auto font-medium">{fmtArgent(r.montant)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${CLASSES_STATUT[r.statut]}`}>{r.statut_libelle}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
